import { readCachedUser } from "../utils/cachedUser";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import NotificationBell from "../components/NotificationBell";
import { perfMark } from "../utils/perf";
import Icon from "../components/Icon";
import useIncomeTransactions, { byNewest } from "../hooks/useIncomeTransactions";

const css = `
*{box-sizing:border-box;margin:0;padding:0;}
  .tp{min-height:100vh;background:var(--bg);color:var(--text);font-family:'Prompt',sans-serif;}
  .tp-nav{display:flex;align-items:center;justify-content:space-between;padding:18px 40px;background:var(--surface);border-bottom:1px solid var(--border);position:sticky;top:0;z-index:10;}
  .tp-logo{display:flex;align-items:center;}
  .tp-logo span{color:var(--text);}
  .tp-logo-img{height:80px;width:auto;mix-blend-mode:screen;filter:brightness(1.1);}
  .tp-back{padding:7px 16px;border-radius:var(--radius-full);border:1px solid var(--border);background:transparent;color:var(--text-muted);font-family:'Prompt',sans-serif;font-size:0.8rem;cursor:pointer;transition:all 0.2s;}
  .tp-back:hover{border-color:var(--accent);color:var(--accent);}
  .tp-body{max-width:760px;margin:0 auto;padding:32px 24px;}
  .tp-title{font-family:'DM Serif Display',serif;font-size:1.6rem;margin-bottom:4px;}
  .tp-subtitle{font-size:0.8rem;color:var(--text-muted);margin-bottom:24px;}

  /* Tabs */
  .tp-tabs{display:flex;gap:4px;background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:4px;margin-bottom:24px;}
  .tp-tab{flex:1;padding:10px 16px;border-radius:9px;border:none;background:transparent;color:var(--text-muted);font-family:'Prompt',sans-serif;font-size:0.85rem;font-weight:600;cursor:pointer;transition:all 0.2s;display:flex;align-items:center;justify-content:center;gap:6px;}
  .tp-tab.active{background:rgba(247,71,53,0.12);color:var(--accent);border:1px solid rgba(247,71,53,0.2);}
  .tp-tab-badge{background:var(--accent);color:#f5f6f8;font-size:0.65rem;font-weight:700;padding:1px 6px;border-radius:100px;min-width:18px;text-align:center;}
  .tp-tab.active .tp-tab-badge{background:#f5f6f8;color:var(--accent);}

  /* Filter bar */
  .tp-filter{display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap;}
  .tp-filter-btn{padding:6px 14px;border-radius:var(--radius-full);border:1px solid var(--border);background:transparent;color:var(--text-muted);font-family:'Prompt',sans-serif;font-size:0.75rem;cursor:pointer;transition:all 0.2s;}
  .tp-filter-btn.active{background:rgba(247,71,53,0.1);border-color:rgba(247,71,53,0.3);color:var(--accent);}

  /* Search */
  .tp-search{width:100%;padding:10px 14px;background:var(--surface);border:1px solid var(--border);border-radius:10px;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.85rem;outline:none;margin-bottom:16px;transition:border-color 0.2s;}
  .tp-search:focus{border-color:rgba(247,71,53,0.3);}
  .tp-search::placeholder{color:var(--text-muted);}

  /* Summary cards */
  .tp-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:24px;}
  .tp-sum-card{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:14px 16px;}
  .tp-sum-label{font-size:0.68rem;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-muted);margin-bottom:6px;}
  .tp-sum-value{font-family:'DM Serif Display',serif;font-size:1.35rem;color:var(--text);}
  .tp-sum-value.accent{color:var(--accent);}
  .tp-sum-value.danger{color:var(--danger);}

  /* Deal List */
  .tp-list{display:flex;flex-direction:column;gap:10px;}
  .tp-deal{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:16px;cursor:pointer;transition:all 0.2s;display:flex;align-items:flex-start;gap:14px;}
  .tp-deal:hover{border-color:rgba(247,71,53,0.3);background:var(--surface2);}
  .tp-deal-icon{width:44px;height:44px;border-radius:11px;display:flex;align-items:center;justify-content:center;font-size:1.25rem;flex-shrink:0;}
  .tp-deal-icon.buy{background:rgba(85,153,255,0.1);}
  .tp-deal-icon.sell{background:rgba(247,71,53,0.08);}
  .tp-deal-body{flex:1;min-width:0;}
  .tp-deal-name{font-size:0.92rem;font-weight:600;margin-bottom:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .tp-deal-meta{font-size:0.73rem;color:var(--text-muted);display:flex;gap:10px;flex-wrap:wrap;align-items:center;}
  .tp-deal-right{text-align:right;flex-shrink:0;}
  .tp-deal-price{font-size:1rem;font-weight:700;color:var(--accent);margin-bottom:4px;}
  .tp-deal-price.minus{color:var(--danger);}
  .tp-deal-date{font-size:0.7rem;color:var(--text-muted);}
  .tp-role-badge{display:inline-block;padding:2px 8px;border-radius:100px;font-size:0.68rem;font-weight:600;}
  .tp-role-badge.buyer{background:rgba(85,153,255,0.1);color:var(--info);}
  .tp-role-badge.seller{background:rgba(247,71,53,0.08);color:var(--accent);}
  .tp-done-badge{display:inline-flex;align-items:center;gap:3px;padding:2px 8px;border-radius:100px;font-size:0.68rem;font-weight:600;background:rgba(247,71,53,0.12);color:var(--accent);border:1px solid rgba(247,71,53,0.2);}

  /* Wallet TX */
  .tp-wtx{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:14px 16px;display:flex;align-items:center;gap:14px;}
  .tp-wtx-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:1.15rem;flex-shrink:0;}
  .tp-wtx-icon.topup{background:rgba(247,71,53,0.1);}
  .tp-wtx-icon.lock{background:rgba(255,179,71,0.1);}
  .tp-wtx-icon.release{background:rgba(247,71,53,0.12);}
  .tp-wtx-icon.refund{background:rgba(85,153,255,0.1);}
  .tp-wtx-icon.withdraw{background:rgba(255,85,85,0.1);}
  .tp-wtx-body{flex:1;}
  .tp-wtx-name{font-size:0.88rem;font-weight:600;margin-bottom:2px;}
  .tp-wtx-sub{font-size:0.72rem;color:var(--text-muted);}
  .tp-wtx-right{text-align:right;}
  .tp-wtx-amount{font-size:0.95rem;font-weight:700;}
  .tp-wtx-amount.plus{color:var(--accent);}
  .tp-wtx-amount.minus{color:var(--danger);}
  .tp-wtx-date{font-size:0.7rem;color:var(--text-muted);margin-top:2px;}

  .tp-empty{text-align:center;padding:56px 24px;color:var(--text-muted);}
  .tp-empty-icon{font-size:2.8rem;margin-bottom:12px;}
  .tp-empty-text{font-size:0.9rem;font-weight:600;color:var(--text);margin-bottom:6px;}
  .tp-empty-sub{font-size:0.78rem;color:#5a7264;line-height:1.6;}

  .tp-loading{display:flex;align-items:center;justify-content:center;padding:56px;gap:10px;color:var(--text-muted);font-size:0.85rem;}
  .tp-spinner{width:18px;height:18px;border:2px solid rgba(247,71,53,0.2);border-top-color:var(--accent);border-radius:50%;animation:spin 0.8s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg)}}

  @media(max-width:600px){
    .tp-nav{padding:12px 14px;}
    .tp-body{padding:18px 14px;}
    .tp-summary{grid-template-columns:1fr 1fr;}
    .tp-summary .tp-sum-card:last-child{grid-column:1/-1;}
    .tp-tab{font-size:0.76rem;padding:9px 10px;}
    .tp-deal{gap:10px;padding:12px 12px;}
    .tp-deal-icon{width:36px;height:36px;font-size:1.1rem;}
    .tp-deal-name{font-size:0.85rem;}
    .tp-deal-price{font-size:0.9rem;}
    .tp-wtx{gap:10px;padding:12px 12px;}
    .tp-wtx-icon{width:34px;height:34px;font-size:1rem;}
    .tp-title{font-size:1.35rem;}
    .tp-filter-btn{padding:5px 10px;font-size:0.7rem;}
  }
  @media(max-width:380px){
    .tp-summary{grid-template-columns:1fr;}
    .tp-summary .tp-sum-card:last-child{grid-column:auto;}
    .tp-tab-badge{display:none;}
  }
`;

const TX_META = {
  topup:   { icon: "wallet", label: "เติมเงิน (PromptPay)",       type: "plus"  },
  lock:    { icon: "lock", label: "ชำระเงิน (วางมัดจำ Escrow)", type: "minus" },
  release: { icon: "check", label: "รับเงิน (ขายสำเร็จ)",        type: "plus"  },
  coupon_share: { icon: "coin", label: "ส่วนแบ่งจากคูปอง",           type: "plus"  },
  withdraw_reject: { icon: "undo", label: "ถอนเงินไม่สำเร็จ (คืนเงิน)", type: "plus" },
  refund:  { icon: "undo", label: "คืนเงิน",                    type: "plus"  },
  withdraw:{ icon: "bank", label: "ถอนเงิน",                    type: "minus" },
};

const DEAL_FILTERS = [
  { key: "all",  label: "ทั้งหมด" },
  { key: "buy",  label: "ที่ซื้อ"  },
  { key: "sell", label: "ที่ขาย"  },
];

const TX_FILTERS = [
  { key: "all",     label: "ทั้งหมด"    },
  { key: "topup",   label: "เติมเงิน"   },
  { key: "lock",    label: "ชำระค่าสินค้า" },
  { key: "release", label: "รับเงิน"    },
  { key: "coupon_share", label: "ส่วนแบ่งคูปอง" },
  { key: "refund",  label: "คืนเงิน"    },
  { key: "withdraw",label: "ถอนเงิน"    },
];

function formatDate(ts) {
  if (!ts) return "-";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("th-TH", {
    day: "numeric", month: "short", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function TransactionPage() {
  const navigate = useNavigate();
  const user = readCachedUser();

  const [tab,         setTab]         = useState("deals");
  const [deals,       setDeals]       = useState([]);
  const [ownWalletTxs, setWalletTxs] = useState([]);
  const [loadingDeals,setLoadingDeals]= useState(true);
  const [loadingTxs,  setLoadingTxs]  = useState(true);
  const [dealFilter,  setDealFilter]  = useState("all");
  const [txFilter,    setTxFilter]    = useState("all");
  const [search,      setSearch]      = useState("");

  useEffect(() => { if (!user.uid) navigate("/login"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── โหลดดีลที่เสร็จสิ้นแล้ว (status === "released") ───────────────────────
  useEffect(() => {
    if (!user.uid) return;

    let buyerDeals  = [];
    let sellerDeals = [];
    const doneFirst = perfMark("TransactionPage: onSnapshot(deals) ครั้งแรก");
    let first = true;

    const merge = () => {
      const map = new Map();
      [...buyerDeals, ...sellerDeals].forEach((d) => {
        if (!map.has(d.id)) map.set(d.id, d);
      });
      const list = [...map.values()].sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );
      if (first) { doneFirst(list.length + " ดีล"); first = false; }
      setDeals(list);
      setLoadingDeals(false);
    };

    // Query 1: ดีลที่ user เป็น buyer และเสร็จสิ้น (ไม่ใช้ orderBy เพื่อหลีกเลี่ยง composite index)
    // หมายเหตุ: ดีลที่เสร็จสิ้นจริงจะถูกบันทึกเป็น status "released" (ดู escrowRelease ใน functions/escrow.js)
    const qBuyer = query(
      collection(db, "rooms"),
      where("buyerId", "==", user.uid),
      where("status",  "==", "released")
    );
    // Query 2: ดีลที่ user เป็น seller และเสร็จสิ้น
    const qSeller = query(
      collection(db, "rooms"),
      where("sellerId", "==", user.uid),
      where("status",   "==", "released")
    );

    const onErr = (err) => {
      if (first) { doneFirst("error: " + err.code); first = false; }
      console.error("[TransactionPage] deals listener error:", err);
      setLoadingDeals(false);
      if (err.code === "permission-denied" || err.code === "unauthenticated") {
        localStorage.removeItem("klang-user");
        navigate("/login");
      }
    };
    const unsub1 = onSnapshot(qBuyer,  (snap) => {
      buyerDeals  = snap.docs.map((d) => ({ id: d.id, _role: "buyer",  ...d.data() }));
      merge();
    }, onErr);
    const unsub2 = onSnapshot(qSeller, (snap) => {
      sellerDeals = snap.docs.map((d) => ({ id: d.id, _role: "seller", ...d.data() }));
      merge();
    }, onErr);

    return () => { unsub1(); unsub2(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── โหลด wallet transactions ──────────────────────────────────────────────
  useEffect(() => {
    if (!user.uid) return;
    const doneFirst = perfMark("TransactionPage: onSnapshot(transactions) ครั้งแรก");
    let first = true;
    const q = query(
      collection(db, "transactions"),
      where("userId", "==", user.uid)
    );
    return onSnapshot(q, (snap) => {
      if (first) { doneFirst(snap.size + " รายการ"); first = false; }
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setWalletTxs(list);
      setLoadingTxs(false);
    }, () => setLoadingTxs(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // รวมรายการที่ผู้ใช้ "ได้รับเงิน" ในฐานะผู้ขาย/เจ้าของคูปอง (ไม่มี userId ในธุรกรรม จึงค้นด้วย userId อย่างเดียวไม่เจอ)
  const incomeTxs = useIncomeTransactions(user.uid);
  const walletTxs = useMemo(() => [...ownWalletTxs, ...incomeTxs].sort(byNewest), [ownWalletTxs, incomeTxs]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalBuyDeals  = deals.filter((d) => d._role === "buyer").length;
  const totalSellDeals = deals.filter((d) => d._role === "seller").length;
  const totalSpent     = deals.filter((d) => d._role === "buyer").reduce((s, d) => s + (d.price || 0), 0);
  const totalEarned    = deals.filter((d) => d._role === "seller").reduce((s, d) => s + (d.price || 0), 0);

  const totalTopup     = walletTxs.filter((t) => t.type === "topup").reduce((s, t) => s + (t.amount || 0), 0);
  const totalWithdraw  = walletTxs.filter((t) => t.type === "withdraw").reduce((s, t) => s + (t.amount || 0), 0);
  const totalRelease   = walletTxs.filter((t) => t.type === "release").reduce((s, t) => s + (t.amount || 0), 0);

  // ── Filtered deals ────────────────────────────────────────────────────────
  const filteredDeals = deals.filter((d) => {
    if (dealFilter === "buy"  && d._role !== "buyer")  return false;
    if (dealFilter === "sell" && d._role !== "seller") return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        (d.itemName   || "").toLowerCase().includes(s) ||
        (d.roomCode   || "").toLowerCase().includes(s) ||
        (d.buyerName  || "").toLowerCase().includes(s) ||
        (d.sellerName || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  // ── Filtered wallet txs ───────────────────────────────────────────────────
  const filteredTxs = walletTxs.filter((t) =>
    txFilter === "all" || t.type === txFilter
  );

  // ── Render deal row ───────────────────────────────────────────────────────
  const renderDeal = (deal) => {
    const isBuyer      = deal._role === "buyer";
    const icon         = isBuyer ? "cart" : "box";
    const counterName  = isBuyer
      ? (deal.sellerName || "ผู้ขาย")
      : (deal.buyerName  || "ผู้ซื้อ");

    return (
      <div
        key={deal.id}
        className="tp-deal"
        onClick={() => navigate(`/chat/${deal.id}`)}
        title="คลิกดูรายละเอียดในห้องแชท"
      >
        <div className={`tp-deal-icon ${deal._role}`}><Icon name={icon} size={20} /></div>

        <div className="tp-deal-body">
          <div className="tp-deal-name">{deal.itemName || "(ไม่มีชื่อสินค้า)"}</div>
          <div className="tp-deal-meta">
            <span className={`tp-role-badge ${deal._role}`}>
              {isBuyer ? "ผู้ซื้อ" : "ผู้ขาย"}
            </span>
            <span>{counterName}</span>
            {deal.roomCode && (
              <span style={{ fontFamily: "'DM Mono',monospace", color: "var(--accent)", fontSize: "0.7rem" }}>
                #{deal.roomCode}
              </span>
            )}
          </div>
        </div>

        <div className="tp-deal-right">
          <div className={`tp-deal-price${isBuyer ? " minus" : ""}`}>
            {isBuyer ? "-" : "+"}฿{(deal.price || 0).toLocaleString()}
          </div>
          <div className="tp-deal-date">{formatDate(deal.createdAt)}</div>
          <div className="tp-done-badge" style={{ marginTop: 4, float: "right" }}>
            เสร็จสิ้น
          </div>
        </div>
      </div>
    );
  };

  // ── Render wallet tx row ──────────────────────────────────────────────────
  const renderWalletTx = (tx) => {
    const meta   = TX_META[tx.type] || { icon: "coin", label: tx.type, type: "plus" };
    const isPlus = meta.type === "plus";
    return (
      <div key={tx.id} className="tp-wtx">
        <div className={`tp-wtx-icon ${tx.type || "topup"}`}><Icon name={meta.icon} size={18} /></div>
        <div className="tp-wtx-body">
          <div className="tp-wtx-name">{meta.label}</div>
          <div className="tp-wtx-sub">
            {tx.fee && tx.type !== "release" && tx.type !== "coupon_share" ? `ค่าธรรมเนียม ฿${tx.fee.toLocaleString()}` : ""}
            {tx.itemName? ` · ${tx.itemName}` : ""}
            {tx.roomCode? ` · #${tx.roomCode}` : ""}
          </div>
        </div>
        <div className="tp-wtx-right">
          <div className={`tp-wtx-amount ${isPlus ? "plus" : "minus"}`}>
            {isPlus ? "+" : "-"}฿{(tx.amount || 0).toLocaleString()}
          </div>
          <div className="tp-wtx-date">{formatDate(tx.createdAt)}</div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="tp">
      <style>{css}</style>

      <nav className="tp-nav">
        <div className="tp-logo" onClick={() => navigate("/home")} style={{cursor:"pointer"}}><span className="kl-wordmark">klang.</span></div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <NotificationBell />
          <button className="tp-back" onClick={() => navigate("/home")}>← กลับ</button>
        </div>
      </nav>

      <div className="tp-body">
        <div className="tp-title">ประวัติธุรกรรม</div>
        <div className="tp-subtitle">บันทึกการซื้อขายและกระเป๋าเงินของคุณ</div>

        {/* ── Tabs ─────────────────────────────────────────────────────────── */}
        <div className="tp-tabs">
          <button
            className={`tp-tab${tab === "deals" ? " active" : ""}`}
            onClick={() => { setTab("deals"); setSearch(""); }}
          >
            ธุรกรรมการซื้อขาย
            {deals.length > 0 && <span className="tp-tab-badge">{deals.length}</span>}
          </button>
          <button
            className={`tp-tab${tab === "wallet" ? " active" : ""}`}
            onClick={() => { setTab("wallet"); setSearch(""); }}
          >
            เงินเข้า-ออก Wallet
            {walletTxs.length > 0 && <span className="tp-tab-badge">{walletTxs.length}</span>}
          </button>
        </div>

        {/* ══ TAB: ธุรกรรมการซื้อขาย ═════════════════════════════════════════ */}
        {tab === "deals" && (
          <>
            {/* Summary */}
            <div className="tp-summary">
              <div className="tp-sum-card">
                <div className="tp-sum-label">ทำรายการทั้งหมด</div>
                <div className="tp-sum-value accent">{deals.length} รายการ</div>
              </div>
              <div className="tp-sum-card">
                <div className="tp-sum-label">ยอดซื้อรวม</div>
                <div className="tp-sum-value" style={{ fontSize: "1.1rem" }}>
                  ฿{totalSpent.toLocaleString()}
                  <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginLeft: 4 }}>
                    ({totalBuyDeals} รายการ)
                  </span>
                </div>
              </div>
              <div className="tp-sum-card">
                <div className="tp-sum-label">ยอดขายรวม</div>
                <div className="tp-sum-value accent" style={{ fontSize: "1.1rem" }}>
                  ฿{totalEarned.toLocaleString()}
                  <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginLeft: 4 }}>
                    ({totalSellDeals} รายการ)
                  </span>
                </div>
              </div>
            </div>

            {/* Filter */}
            <div className="tp-filter">
              {DEAL_FILTERS.map((f) => (
                <button
                  key={f.key}
                  className={`tp-filter-btn${dealFilter === f.key ? " active" : ""}`}
                  onClick={() => setDealFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <input
              className="tp-search"
              placeholder="ค้นหาชื่อสินค้า, รหัสห้อง, ชื่อผู้ซื้อ/ขาย..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {/* List */}
            {loadingDeals ? (
              <div className="tp-loading"><div className="tp-spinner" /> กำลังโหลด...</div>
            ) : filteredDeals.length === 0 ? (
              <div className="tp-empty">
                <div className="tp-empty-icon"><Icon name="check" size={40} /></div>
                <div className="tp-empty-text">
                  {search ? "ไม่พบรายการที่ค้นหา" : "ยังไม่มีการซื้อขายที่เสร็จสิ้น"}
                </div>
                <div className="tp-empty-sub">
                  {search
                    ? "ลองค้นหาด้วยคำอื่น"
                    : "รายการซื้อขายที่เสร็จสิ้นแล้วจะแสดงที่นี่"}
                </div>
              </div>
            ) : (
              <div className="tp-list">{filteredDeals.map(renderDeal)}</div>
            )}
          </>
        )}

        {/* ══ TAB: เงินเข้า-ออก Wallet ════════════════════════════════════════ */}
        {tab === "wallet" && (
          <>
            {/* Summary */}
            <div className="tp-summary">
              <div className="tp-sum-card">
                <div className="tp-sum-label">เติมเงินรวม</div>
                <div className="tp-sum-value accent" style={{ fontSize: "1.1rem" }}>
                  ฿{totalTopup.toLocaleString()}
                </div>
              </div>
              <div className="tp-sum-card">
                <div className="tp-sum-label">ถอนเงินรวม</div>
                <div className="tp-sum-value danger" style={{ fontSize: "1.1rem" }}>
                  ฿{totalWithdraw.toLocaleString()}
                </div>
              </div>
              <div className="tp-sum-card">
                <div className="tp-sum-label">รับจากการขาย</div>
                <div className="tp-sum-value accent" style={{ fontSize: "1.1rem" }}>
                  ฿{totalRelease.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Filter */}
            <div className="tp-filter">
              {TX_FILTERS.map((f) => (
                <button
                  key={f.key}
                  className={`tp-filter-btn${txFilter === f.key ? " active" : ""}`}
                  onClick={() => setTxFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* List */}
            {loadingTxs ? (
              <div className="tp-loading"><div className="tp-spinner" /> กำลังโหลด...</div>
            ) : filteredTxs.length === 0 ? (
              <div className="tp-empty">
                <div className="tp-empty-icon"><Icon name="coin" size={40} /></div>
                <div className="tp-empty-text">
                  {txFilter !== "all" ? "ไม่พบรายการประเภทนี้" : "ยังไม่มีธุรกรรมทางการเงิน"}
                </div>
                <div className="tp-empty-sub">
                  {txFilter !== "all" ? "ลองเปลี่ยนตัวกรอง" : "ประวัติการเติมเงิน ถอนเงิน และรับเงินจะแสดงที่นี่"}
                </div>
              </div>
            ) : (
              <div className="tp-list">{filteredTxs.map(renderWalletTx)}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
